import * as React from 'react';
import { useState } from 'react';
import { Button, Flex, FlexItem, FormGroup, FormSelect, FormSelectOption, HelperText, HelperTextItem, Stack, StackItem, Switch, TextInput } from '@patternfly/react-core';
import { AngleDownIcon, AngleRightIcon } from '@patternfly/react-icons';

import { GroupPicker, patternError, patternPreview } from './GroupPicker';
import { Group } from './parseGroups';
import { ClusterRule, Fetcher } from './types';

/** A rule plus a client-only id, so collapsing/removing rules never mixes up their open state. */
export type DraftRule = ClusterRule & { uid: string };

let nextUid = 0;
export const withUid = (rule: ClusterRule): DraftRule => ({ ...rule, uid: `rule-${nextUid++}` });

const ruleSummary = (rule: DraftRule): string => {
  const parts: string[] = [];
  if (rule.groups.length > 0) parts.push(`${rule.groups.length} group${rule.groups.length === 1 ? '' : 's'}`);
  if (rule.groupPattern?.trim()) parts.push(`pattern ${rule.groupPattern.trim()}`);
  return parts.length > 0 ? parts.join(' + ') : 'no groups yet';
};

const RuleRow: React.FC<{
  rule: DraftRule;
  idPrefix: string;
  clusters: string[];
  onChange: (patch: Partial<ClusterRule>) => void;
  onRemove: () => void;
  api: Fetcher;
  suggestions: Group[];
  mine: Group[];
}> = ({ rule, idPrefix, clusters, onChange, onRemove, api, suggestions, mine }) => {
  // A brand-new rule opens so it can be filled in; saved rules stay folded away.
  const [open, setOpen] = useState(rule.groups.length === 0 && !rule.groupPattern);
  const error = patternError(rule.groupPattern);

  return (
    <div style={{ border: '1px solid var(--pf-v5-global--BorderColor--100)', borderRadius: 4, padding: '8px 12px' }}>
      <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsCenter' }} flexWrap={{ default: 'nowrap' }}>
        <FlexItem>
          <Button
            variant="link"
            isInline
            aria-expanded={open}
            icon={open ? <AngleDownIcon /> : <AngleRightIcon />}
            onClick={() => setOpen(o => !o)}
          >
            {rule.cluster}
          </Button>{' '}
          <span style={{ opacity: 0.7 }}>{ruleSummary(rule)}</span>
        </FlexItem>
        <FlexItem>
          <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }} flexWrap={{ default: 'nowrap' }}>
            <FlexItem>
              <Switch
                id={`${idPrefix}-${rule.uid}-on`}
                aria-label={`${rule.cluster} rule on`}
                label="On"
                isChecked={rule.enabled}
                onChange={(_e, checked) => onChange({ enabled: checked })}
              />
            </FlexItem>
            <FlexItem>
              <Button variant="link" isDanger isInline onClick={onRemove}>
                Remove
              </Button>
            </FlexItem>
          </Flex>
        </FlexItem>
      </Flex>

      {open && (
        <Stack hasGutter style={{ marginTop: 12 }}>
          <StackItem>
            <FormGroup label="Cluster" fieldId={`${idPrefix}-${rule.uid}-cluster`}>
              <FormSelect
                id={`${idPrefix}-${rule.uid}-cluster`}
                aria-label="Cluster"
                value={rule.cluster}
                onChange={(_e, v) => onChange({ cluster: v })}
                style={{ width: 200 }}
              >
                {clusters.map(c => (
                  <FormSelectOption key={c} value={c} label={c} />
                ))}
              </FormSelect>
            </FormGroup>
          </StackItem>
          <StackItem>
            <div>
              Members of these groups can only use <strong>{rule.cluster}</strong>:
            </div>
            <GroupPicker value={rule.groups} onChange={groups => onChange({ groups })} api={api} suggestions={suggestions} mine={mine} />
          </StackItem>
          <StackItem>
            <FormGroup label="Also match groups by name (pattern)" fieldId={`${idPrefix}-${rule.uid}-pattern`}>
              <TextInput
                id={`${idPrefix}-${rule.uid}-pattern`}
                aria-label="Group name pattern"
                placeholder="e.g. ^AD-ITP-Team-.+-Developers$"
                value={rule.groupPattern ?? ''}
                onChange={(_e, v) => onChange({ groupPattern: v })}
              />
              <HelperText>
                <HelperTextItem variant={error ? 'error' : 'indeterminate'}>{error ?? patternPreview(rule.groupPattern, [...suggestions, ...mine])}</HelperTextItem>
              </HelperText>
            </FormGroup>
          </StackItem>
        </Stack>
      )}
    </div>
  );
};

export const ClusterRulesEditor: React.FC<{
  rules: DraftRule[];
  onChange: (rules: DraftRule[]) => void;
  clusters: string[];
  idPrefix: string;
  api: Fetcher;
  suggestions: Group[];
  mine: Group[];
}> = ({ rules, onChange, clusters, idPrefix, api, suggestions, mine }) => {
  const unused = clusters.filter(c => !rules.some(r => r.cluster === c));

  return (
    <Stack hasGutter>
      {rules.length === 0 && (
        <StackItem>
          <em>No rules. Add one to restrict a group to a cluster.</em>
        </StackItem>
      )}
      {rules.map(rule => (
        <StackItem key={rule.uid}>
          <RuleRow
            rule={rule}
            idPrefix={idPrefix}
            clusters={[rule.cluster, ...unused]}
            onChange={patch => onChange(rules.map(r => (r.uid === rule.uid ? { ...r, ...patch } : r)))}
            onRemove={() => onChange(rules.filter(r => r.uid !== rule.uid))}
            api={api}
            suggestions={suggestions}
            mine={mine}
          />
        </StackItem>
      ))}
      <StackItem>
        <Button variant="secondary" isDisabled={unused.length === 0} onClick={() => onChange([...rules, withUid({ cluster: unused[0], enabled: true, groups: [] })])}>
          Add rule
        </Button>
      </StackItem>
    </Stack>
  );
};
